// Client-only Vibe realtime socket (singleton). One WebSocket per app to the App DO gateway,
// shared by all hooks (useVibeQuery invalidation, usePresence, useBroadcast). Never import from
// server code.
//
// Auth: the token fetcher (registered by the template via setVibeWsTokenFetcher) asks the app's own
// Lambda for a short-lived opaque WS token; then connect ws?aiAppId=&token=. The Lambda is the
// identity authority (member via monday session, guest via viewer id) — the browser never talks to
// the builder backend. The token is only validated at upgrade (the DO never re-checks a live
// connection), so there is NO proactive refresh — every (re)connect mints a fresh token, and drops
// reconnect with jittered backoff.
//
// The __VIBE_*__ placeholders are injected at deploy time (same pattern as vibe-auth.ts). When not
// injected (local dev / no DO) or no fetcher is registered, the socket is disabled and every method
// is a safe no-op, so generated apps still run.
import type { ServerMessage, InvalidateChange, Peer } from './vibe-realtime';

const RAW_WS_URL = '__VIBE_DO_WS_URL__';
const VIBE_DO_WS_URL: string = RAW_WS_URL.includes('__VIBE_') ? '' : RAW_WS_URL;

// The app's own id, injected at deploy time; appended to the /ws URL so the gateway routes to the
// right DO (the DO then validates the opaque token against its own store).
const RAW_AI_APP_ID = '__VIBE_AI_APP_ID__';
const VIBE_AI_APP_ID: number = RAW_AI_APP_ID.includes('__VIBE_') ? 0 : Number(RAW_AI_APP_ID);

/** Fetches a fresh opaque WS token from the app's Lambda. Registered once by the template. */
export type WsTokenFetcher = () => Promise<{ token: string; expiresInSeconds: number }>;
let moduleTokenFetcher: WsTokenFetcher | undefined;
export function setVibeWsTokenFetcher(fn: WsTokenFetcher): void {
  moduleTokenFetcher = fn;
}

const INVALIDATE_COALESCE_MS = 100;
const MAX_BACKOFF_MS = 30_000;

type InvalidateHandler = (collections: Set<string>) => void;
type PresenceHandler = (peers: Peer[]) => void;
type BroadcastHandler = (payload: unknown, from: string) => void;

export interface VibeSocket {
  connect(): void;
  onInvalidate(handler: InvalidateHandler): () => void;
  onPresence(handler: PresenceHandler): () => void;
  getPresence(): Peer[];
  onBroadcast(event: string, handler: BroadcastHandler): () => void;
  sendBroadcast(event: string, payload: unknown): void;
  /** Fires after every reconnection beyond the first, so consumers can reconcile missed state. */
  onReconnect(handler: () => void): () => void;
  readonly enabled: boolean;
}

class VibeSocketImpl implements VibeSocket {
  // URL/id default to the deploy-injected placeholders; the constructor seam lets tests inject them,
  // including a token fetcher (falls back to the module-registered one).
  constructor(
    private readonly wsUrl: string = VIBE_DO_WS_URL,
    private readonly aiAppId: number = VIBE_AI_APP_ID,
    private readonly injectedFetcher?: WsTokenFetcher,
  ) {}

  private get tokenFetcher(): WsTokenFetcher | undefined {
    return this.injectedFetcher ?? moduleTokenFetcher;
  }

  get enabled(): boolean {
    return Boolean(this.wsUrl && this.aiAppId && this.tokenFetcher);
  }

  private ws?: WebSocket;
  private started = false;
  private closedByUs = false;
  private backoff = 500;
  private hasConnectedBefore = false;

  private peers: Peer[] = [];
  private readonly invalidateHandlers = new Set<InvalidateHandler>();
  private readonly presenceHandlers = new Set<PresenceHandler>();
  private readonly reconnectHandlers = new Set<() => void>();
  private readonly broadcastHandlers = new Map<string, Set<BroadcastHandler>>();

  // Invalidation coalescing: buffer change collections for a short window so a batch write that
  // emits one message (or a burst) triggers a single refetch per collection.
  private pendingCollections = new Set<string>();
  private coalesceTimer?: ReturnType<typeof setTimeout>;

  connect(): void {
    if (!this.enabled || this.started) return;
    this.started = true;
    this.closedByUs = false;
    void this.openSocket();
  }

  private async openSocket(): Promise<void> {
    let token: string;
    try {
      const fetcher = this.tokenFetcher;
      if (!fetcher) throw new Error('no ws token fetcher registered');
      const body = await fetcher();
      token = body.token;
    } catch (err: any) {
      console.warn(`[vibe-socket] token fetch failed: ${err?.message || err}`);
      this.scheduleReconnect();
      return;
    }

    try {
      // Gateway routes by aiAppId; the DO validates the opaque token. wsUrl may already carry ?jur=eu.
      const sep = this.wsUrl.includes('?') ? '&' : '?';
      const url = `${this.wsUrl}${sep}aiAppId=${this.aiAppId}&token=${encodeURIComponent(token)}`;
      const ws = new WebSocket(url);
      this.ws = ws;
      ws.addEventListener('open', () => {
        const isReconnect = this.hasConnectedBefore;
        this.hasConnectedBefore = true;
        this.backoff = 500;
        // A reconnect (network drop / DO restart) may have missed invalidations while offline, and
        // its presence snapshot is stale — tell consumers to reconcile. The DO re-sends presence on
        // connect, so peers repopulate on their own.
        if (isReconnect) {
          this.reconnectHandlers.forEach((h) => h());
        }
      });
      ws.addEventListener('message', (ev) => this.onMessage(ev));
      ws.addEventListener('close', () => this.onClose(ws));
      ws.addEventListener('error', () => ws.close());
    } catch (err: any) {
      console.warn(`[vibe-socket] connect failed: ${err?.message || err}`);
      this.scheduleReconnect();
    }
  }

  private onMessage(ev: MessageEvent): void {
    let msg: ServerMessage;
    try {
      msg = JSON.parse(typeof ev.data === 'string' ? ev.data : '') as ServerMessage;
    } catch {
      return;
    }
    switch (msg.type) {
      case 'invalidate':
        this.bufferInvalidations(msg.changes);
        break;
      case 'presence':
        this.peers = msg.peers;
        this.presenceHandlers.forEach((h) => h(this.peers));
        break;
      case 'broadcast': {
        const handlers = this.broadcastHandlers.get(msg.event);
        handlers?.forEach((h) => h(msg.payload, msg.from));
        break;
      }
    }
  }

  private bufferInvalidations(changes: InvalidateChange[]): void {
    for (const c of changes) this.pendingCollections.add(c.collection);
    if (this.coalesceTimer) return;
    this.coalesceTimer = setTimeout(() => {
      this.coalesceTimer = undefined;
      const collections = this.pendingCollections;
      this.pendingCollections = new Set();
      if (collections.size > 0) this.invalidateHandlers.forEach((h) => h(collections));
    }, INVALIDATE_COALESCE_MS);
  }

  private onClose(ws: WebSocket): void {
    // Ignore close events from a socket we've already replaced (e.g. a token refresh opened a new
    // one, or a late close from a prior connection). Without this guard we'd null out the live
    // socket AND spawn a reconnect → duplicate connections (shows as the same viewer N times).
    if (ws !== this.ws) return;
    this.ws = undefined;
    // Presence is only valid while connected; drop peers so a disconnect doesn't leave stale
    // "online" avatars lingering until reconnect. The DO sends a fresh snapshot on reconnect.
    if (this.peers.length > 0) {
      this.peers = [];
      this.presenceHandlers.forEach((h) => h(this.peers));
    }
    if (this.closedByUs) return;
    this.scheduleReconnect();
  }

  private scheduleReconnect(): void {
    // Jitter (0.5–1.5x): after a DO restart/purge every client would otherwise retry on the same
    // 500/1k/2k… schedule and thundering-herd the token mint + WS upgrade.
    const delay = this.backoff * (0.5 + Math.random());
    this.backoff = Math.min(this.backoff * 2, MAX_BACKOFF_MS);
    setTimeout(() => {
      if (!this.closedByUs) void this.openSocket();
    }, delay);
  }

  onInvalidate(handler: InvalidateHandler): () => void {
    this.invalidateHandlers.add(handler);
    return () => this.invalidateHandlers.delete(handler);
  }

  onPresence(handler: PresenceHandler): () => void {
    this.presenceHandlers.add(handler);
    handler(this.peers); // emit current snapshot immediately
    return () => this.presenceHandlers.delete(handler);
  }

  getPresence(): Peer[] {
    return this.peers;
  }

  onBroadcast(event: string, handler: BroadcastHandler): () => void {
    let set = this.broadcastHandlers.get(event);
    if (!set) {
      set = new Set();
      this.broadcastHandlers.set(event, set);
    }
    set.add(handler);
    return () => {
      set.delete(handler);
      if (set.size === 0) this.broadcastHandlers.delete(event);
    };
  }

  sendBroadcast(event: string, payload: unknown): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ type: 'broadcast', event, payload }));
    }
  }

  onReconnect(handler: () => void): () => void {
    this.reconnectHandlers.add(handler);
    return () => this.reconnectHandlers.delete(handler);
  }
}

let instance: VibeSocket | undefined;

/** The app-wide realtime socket. Lazily created; connect() is idempotent and safe to call anywhere. */
export function getVibeSocket(): VibeSocket {
  if (!instance) instance = new VibeSocketImpl();
  return instance;
}

/** Test-only: build an isolated socket with an injected ws URL + token fetcher. */
export function __createVibeSocketForTests(wsUrl: string, fetcher: WsTokenFetcher, aiAppId = 1): VibeSocket {
  return new VibeSocketImpl(wsUrl, aiAppId, fetcher);
}

/** Test-only reset of the singleton. */
export function __resetVibeSocketForTests(): void {
  instance = undefined;
}
